import { memo } from 'react';
import styles from '../styles/CombatLogPanel.module.css';
import NarrativeEntry from './NarrativeEntry.jsx';
import NarrativeEntryStructured from './NarrativeEntryStructured';
import ChatMessage from './ChatMessage';
import { mergeLogItems } from '../engine/chat';
import { firstUnseenIndex } from '../engine/log/readMark';
import { onActivateKey } from '../utils/keyActivation';

/**
 * The two log bodies, lifted out of CombatLogPanel when the stacked split
 * became a single tabbed scroller (v7.16.0). They render entries and
 * nothing else - the scroller, the tabs and the prompts stay in the panel.
 */

/**
 * An entry that points at a place on the map, made clickable.
 *
 * A ping's ring is over in half a second, on purpose - it is a gesture,
 * not a mark. THIS is what makes it durable: the sentence stays in the
 * log, and clicking it moves your own camera there. Every client has its
 * own camera, so "look here" is otherwise only an offer.
 */
function pingProps(entry, onLookAt) {
  if (!onLookAt || typeof entry.pingX !== 'number' || typeof entry.pingZ !== 'number') return null;
  const look = () => onLookAt(entry.pingX, entry.pingZ);
  return {
    role: 'button',
    tabIndex: 0,
    title: 'Look at this place on the map',
    className: styles.pingEntry,
    onClick: look,
    onKeyDown: onActivateKey(look),
  };
}

/**
 * ONE BLAST IS ONE THING, not five unrelated lines.
 *
 * Every victim of an area attack shares a `volleyId`, so the log draws them as
 * a block: a header saying what happened and to how many, a rail down the
 * side, and each victim's own line inside keeping the expander it had.
 *
 * WHAT COMES BETWEEN IS PART OF IT. The first version grouped only entries
 * that were literally adjacent, and that made the feature do nothing in the
 * one case it exists for: each victim answers their own prompt, so their
 * dodge roll lands between one attack line and the next and the run never
 * forms. Anything between two members of the same volley is therefore taken
 * into the block - it IS the exchange.
 *
 * NOTHING EVER MOVES. Entries are emitted in the order they arrived, either
 * inside the block or after it; the buffer is only ever flushed forwards. A
 * log that reorders itself is worse than one that repeats itself.
 */
function groupByVolley(entries) {
  // `open` is the volley still accepting members; `between` holds the
  // entries since its last member - inside it, or not, yet.
  const g = { runs: [], open: null, between: [] };
  for (const entry of entries) addToRuns(g, entry);
  closeVolley(g);
  return g.runs;
}

/** Ends the open volley. Whatever trailed its last member was never part of it. */
function closeVolley(g) {
  if (!g.open) return;
  g.runs.push(g.open);
  for (const e of g.between) g.runs.push({ volleyId: null, items: [e] });
  g.between = [];
  g.open = null;
}

function addToRuns(g, entry) {
  const id = entry?.volleyId;
  if (id && g.open?.volleyId === id) {
    g.open.items.push(...g.between, entry);
    g.between = [];
  } else if (id) {
    closeVolley(g);
    g.open = { volleyId: id, items: [entry] };
  } else if (g.open) {
    g.between.push(entry);
  } else {
    g.runs.push({ volleyId: null, items: [entry] });
  }
}

/** What the header says: the weapon, and how many it caught. */
function volleyHeading(items) {
  // The VICTIMS, not everything in the block: the dodge rolls between them
  // belong to the exchange but are not people who were caught.
  const victims = items.filter((e) => e?.volleyId);
  const named = victims.find((e) => e.structured?.slotName || e.slotName);
  const weapon = named?.structured?.slotName || named?.slotName || 'Area attack';
  // THE COUNT IS WHAT THE ATTACK DID, not how many lines reached this log.
  // The local log holds one line per volley - each victim's entry goes to
  // their own tab - and a dodge can leave a single line in the shared one
  // too. Reading `volleySize` means the header says "3 caught" in both, which
  // is the true and useful thing; counting the lines in front of it would say
  // "1 caught" about a grenade that caught three.
  //
  // THE LARGEST, not the first (v8.373.3): a weapon whose victims spawn a
  // second template writes the primaries' lines before it knows who that
  // catches, so only the spawned lines carry the whole count.
  const caught = Math.max(0, ...victims.map((e) => e.volleySize || 0)) || victims.length;
  return `${weapon} · ${caught} caught`;
}

/**
 * The log's runs, each volley as a block with its header. `renderOne`
 * draws one entry; its index is `run * 1000 + position`, which keys it.
 * `expanded` draws every block open (the viewer's "expand all").
 */
function volleyRuns(entries, renderOne, expanded = false) {
  return groupByVolley(entries).map((run, ri) => {
    const rendered = run.items.map((entry, i) => renderOne(entry, ri * 1000 + i));
    // ANY line from a volley gets the block, even one - in both logs. The
    // local log only ever holds one line of a volley, so the header is the
    // only thing that can say it was part of a blast at all; and a grenade
    // that catches three and is dodged by two leaves ONE line in the shared
    // log, which is the visible third of something bigger, not an ordinary
    // attack. `volleySize` is what the header reads, so it is honest either way.
    if (!run.volleyId) return rendered;
    // ONE LINE until opened (v9.7.0): a blast that caught six was six lines and their dodges pushing
    // everything else off the log. `open` is only the starting state and the viewer's "expand all":
    // React leaves the attribute alone between renders, so a block opened by hand stays open.
    return (
      <details
        key={'v-' + run.volleyId}
        className={styles.volleyGroup}
        data-testid="volley-group"
        open={expanded || undefined}
      >
        <summary className={styles.volleyHeader}>{volleyHeading(run.items)}</summary>
        {rendered}
      </details>
    );
  });
}

/** An info entry whose effects are contained inside a preceding structured entry. */
function containedInLocal(entry, log) {
  if (!entry.parentAttackTs || entry.type !== 'info') return false;
  return log.some((e) => e.structured && e.timestamp === entry.parentAttackTs);
}

function localLine(entry, i, log, onLookAt) {
  if (containedInLocal(entry, log)) return null;
  const key = 'l-' + (entry.timestamp || 0) + '-' + i;
  if (entry.structured) return <NarrativeEntryStructured key={key} entry={entry.structured} />;
  const ping = pingProps(entry, onLookAt);
  return ping ? (
    <div key={key} data-testid="log-ping-entry" {...ping}>
      <NarrativeEntry entry={entry} />
    </div>
  ) : (
    <NarrativeEntry key={key} entry={entry} />
  );
}

/** This browser's own narrative for the active tab. */
function LocalLogEntriesImpl({ log, onLookAt, expanded = false }) {
  if (log.length === 0) {
    return <div className={styles.emptyLogHint}>Select a weapon and press Space to attack.</div>;
  }
  return volleyRuns(log, (entry, i) => localLine(entry, i, log, onLookAt), expanded);
}

function borderColour(text) {
  if (text.startsWith('HIT!')) return 'var(--ok)';
  if (text.startsWith('JAM!')) return 'var(--warn)';
  if (text.startsWith('MISS')) return 'var(--neutral)';
  return 'var(--info-strong)';
}

function chatLine(msg, vox) {
  return (
    <ChatMessage
      key={'c-' + msg.id}
      msg={msg}
      me={vox?.me ?? ''}
      people={vox?.people ?? []}
      onOpenDm={vox?.openDm}
      onRemove={vox?.remove}
      onPin={vox?.pin}
      canRemove={!!vox && (vox.isGm || msg.fromUid === vox.me) && msg.kind !== 'removed'}
    />
  );
}

/** An info entry grouped with a structured attack entry, which already says it. */
function containedInShared(entry, entries) {
  if (!entry.attackGroupId || entry.type !== 'info') return false;
  return entries.some((e) => e.attackGroupId === entry.attackGroupId && e.structured);
}

/** "[Name] → Target", who wrote the line and at whom. */
function metaText(entry) {
  return (
    <>
      [{entry.by || entry.userName || '?'}]{entry.targetName ? ' → ' + entry.targetName : ''}
    </>
  );
}

function structuredSharedLine(entry, key) {
  return (
    <div key={key} className={styles.sharedEntry} style={{ borderLeft: '3px solid var(--info-strong)', padding: 0 }}>
      <span className={styles.sharedEntryMeta} style={{ padding: '4px 6px 0' }}>
        {metaText(entry)}
      </span>
      <NarrativeEntryStructured entry={entry.structured} />
    </div>
  );
}

function plainSharedLine(entry, key, onLookAt) {
  const text = entry.text || '';
  const ping = pingProps(entry, onLookAt);
  return (
    <div
      key={key}
      data-testid={ping ? 'log-ping-entry' : undefined}
      {...(ping || {})}
      className={ping ? `${styles.sharedEntry} ${styles.pingEntry}` : styles.sharedEntry}
      style={{
        borderLeft: '3px solid ' + borderColour(text),
        color: entry.type === 'info' ? 'var(--info-soft)' : 'var(--neutral-bright)',
      }}
    >
      <span className={styles.sharedEntryMeta}>{metaText(entry)}</span>
      {text.split('\n').map((line, li) => (
        <div key={li} className={li > 0 ? styles.sharedEntrySubline : undefined}>
          {line}
        </div>
      ))}
    </div>
  );
}

const NO_LANES = [];

function sharedLine(entry, i, { entries, onLookAt, vox }) {
  if (entry.__divider) return <NewDivider key="new-divider" />;
  if (entry.__chat) return chatLine(entry.__chat, vox);
  if (containedInShared(entry, entries)) return null;
  const key = 'f-' + (entry.id || entry.timestamp || i);
  if (entry.structured && typeof entry.structured === 'object') return structuredSharedLine(entry, key);
  return plainSharedLine(entry, key, onLookAt);
}

/**
 * Everyone's narrative, as replicated through Firestore, with the vox
 * interleaved by time (v8.363.4). `chat` and `hidden` (the lanes switched
 * off, v9.7.0) are optional so
 * every existing caller reads as before; a chat line rides through
 * `groupByVolley` as an object with no volleyId, so it never joins a
 * volley block and never breaks one.
 */
/** The line drawn before the first thing that arrived since the viewer last looked. */
function NewDivider() {
  return (
    <div className={styles.logNewDivider} role="separator" data-testid="log-new-divider">
      <span>New since you looked</span>
    </div>
  );
}

/** The merged lines as the list draws them, with the divider before the first unseen one. */
function withDivider(merged, readAt, reader) {
  const flat = merged.map((it) => (it.kind === 'chat' ? { __chat: it.msg } : it.entry));
  const at = readAt > 0 && reader ? firstUnseenIndex(merged, readAt, reader) : -1;
  // Not before the very first line: a log that is all new needs no line to say so.
  if (at > 0) flat.splice(at, 0, { __divider: true });
  return flat;
}

function SharedLogEntriesImpl({
  entries,
  onLookAt,
  chat = [],
  hidden = NO_LANES,
  vox = null,
  readAt = 0,
  reader,
  expanded = false,
}) {
  const merged = mergeLogItems(entries, chat, hidden);
  if (merged.length === 0)
    return (
      <div className={styles.emptyLogHint}>
        {hidden.length > 0 ? 'Nothing in the lanes you are showing.' : 'No shared entries yet.'}
      </div>
    );
  const flat = withDivider(merged, readAt, reader);
  return volleyRuns(flat, (entry, i) => sharedLine(entry, i, { entries, onLookAt, vox }), expanded);
}

export const LocalLogEntries = memo(LocalLogEntriesImpl);
export const SharedLogEntries = memo(SharedLogEntriesImpl);
